import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/EmptyState';

export default function OPDManagementPage() {
  const [opds, setOpds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, mode: 'create', target: null });
  const empty = { kode: '', nama: '', jenis: 'Dinas', kepala: '', alamat: '' };
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    api.get('/opd').then((r) => setOpds(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = opds.filter((o) => !search || `${o.nama} ${o.kode}`.toLowerCase().includes(search.toLowerCase()));

  const save = async () => {
    try {
      if (modal.mode === 'create') await api.post('/opd', form);
      else await api.patch(`/opd/${modal.target.id}`, form);
      toast.success('Berhasil disimpan');
      setModal({ open: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Hapus OPD ${o.nama}?`)) return;
    try {
      await api.delete(`/opd/${o.id}`);
      toast.success('Berhasil dihapus');
      load();
    } catch (e) {
      toast.error('Gagal');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Referensi OPD"
        subtitle="Master data OPD Pemerintah Kota Makassar."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Manajemen' }, { label: 'OPD' }]}
        action={<Button onClick={() => { setForm(empty); setModal({ open: true, mode: 'create' }); }} className="bg-[#DC2626] hover:bg-[#B91C1C]"><Plus className="w-4 h-4 mr-2" />Tambah OPD</Button>}
      />

      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-4">
          <Input placeholder="Cari nama atau kode OPD…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-premium overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="Belum ada OPD" icon={Building2} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead className="w-32">Kode</TableHead>
                  <TableHead>Nama OPD</TableHead>
                  <TableHead className="w-28">Jenis</TableHead>
                  <TableHead className="hidden lg:table-cell">Kepala OPD</TableHead>
                  <TableHead className="w-28 text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="font-mono text-xs">{o.kode}</TableCell>
                    <TableCell><div className="font-semibold">{o.nama}</div><div className="text-xs text-slate-500">{o.alamat}</div></TableCell>
                    <TableCell className="text-xs text-slate-600">{o.jenis}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs">{o.kepala}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => { setForm({ ...o }); setModal({ open: true, mode: 'edit', target: o }); }}><Edit className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(o)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={modal.open} onOpenChange={(o) => setModal({ ...modal, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{modal.mode === 'create' ? 'Tambah OPD' : 'Edit OPD'}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Kode</Label>
              <Input value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} />
            </div>
            <div>
              <Label>Jenis</Label>
              <Select value={form.jenis} onValueChange={(v) => setForm({ ...form, jenis: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dinas">Dinas</SelectItem>
                  <SelectItem value="Badan">Badan</SelectItem>
                  <SelectItem value="Bagian">Bagian</SelectItem>
                  <SelectItem value="Sekretariat">Sekretariat</SelectItem>
                  <SelectItem value="Kecamatan">Kecamatan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Nama</Label>
              <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Kepala</Label>
              <Input value={form.kepala} onChange={(e) => setForm({ ...form, kepala: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Alamat</Label>
              <Input value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false })}>Batal</Button>
            <Button onClick={save} className="bg-[#DC2626] hover:bg-[#B91C1C]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
